//User profile screen

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { RootState } from '../../store';
import { authService } from '../../services/auth';
import { getAnimalById } from '../../services/firestore';
import { logout } from '../../store/slices/authSlice';
import Button from '../common/Button';
import ColorPicker from './ColorPicker';
import { Animal } from '../../types';
import { ASSET_PATHS } from '../../utils/gameConfig';


interface PersonalProfileProps {
  onBack?: () => void;
}

const PersonalProfile: React.FC<PersonalProfileProps> = ({ onBack }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [userAnimal, setUserAnimal] = useState<Animal | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserAnimal = async () => {
      if (user?.animalId) {
        try {
          const animal = await getAnimalById(user.animalId);
          setUserAnimal(animal);
        } catch (error) {
          console.error('Error loading user animal:', error);
        }
      }
      setIsLoading(false);
    };

    loadUserAnimal();
  }, [user]);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      dispatch(logout());
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleColorChange = (color: string) => {
    console.log('User selected new color:', color);
  };

  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-powder-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sage-200 border-t-sage-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-powder-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.MODAL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="w-[88%] h-[88vh] bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-powder-200 overflow-y-auto">
        <div className="p-6 h-full">
          <div className="max-w-4xl mx-auto h-full flex flex-col space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-powder-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {onBack && (
                <Button variant="outline" onClick={onBack}>
                  ← Back
                </Button>
              )}
              {userAnimal && (
                <div className="relative">
                  <img
                    src={userAnimal.imagePath}
                    alt={userAnimal.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-sage-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `data:image/svg+xml;base64,${btoa(`
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="32" cy="32" r="30" fill="#6a9f6a"/>
                          <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" fill="white">
                            ${user.initials}
                          </text>
                        </svg>
                      `)}`;
                    }}
                  />
                  {user.preferredColor && (
                    <div 
                      className="absolute inset-0 rounded-full border-4 opacity-60"
                      style={{ 
                        borderColor: user.preferredColor
                      }}
                    />
                  )}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-powder-800">
                  Welcome, {user.firstName}!
                </h1>
                <p className="text-powder-600">
                  @{user.username} • {userAnimal?.name || 'Loading...'}
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              {user?.userType === 'instructor' && (
                <Button
                  variant="secondary"
                  onClick={() => window.location.hash = 'teacher'}
                >
                  Teacher Dashboard
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleLogout}
              >
                Log Out
              </Button>
            </div>
          </div>
        </motion.div>

        {/* User Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-powder-200"
        >
          <h2 className="text-xl font-semibold text-powder-800 mb-4">Your Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-powder-600 mb-1">Username</label>
                <p className="text-powder-800 font-medium">{user.username}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-powder-600 mb-1">Full Name</label>
                <p className="text-powder-800 font-medium">{user.firstName} {user.lastName}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-powder-600 mb-1">Initials</label>
                <p className="text-powder-800 font-medium">{user.initials}</p>
              </div>

              {user.email && (
                <div>
                  <label className="block text-sm font-medium text-powder-600 mb-1">Email</label>
                  <p className="text-powder-800 font-medium">{user.email}</p>
                </div>
              )}
            </div>

            {userAnimal && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-powder-600 mb-1">Your Forest Guide</label>
                  <div className="bg-powder-50 rounded-lg p-4 border border-powder-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <img
                        src={userAnimal.imagePath}
                        alt={userAnimal.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <h3 className="font-semibold text-powder-800">{userAnimal.name}</h3>
                    </div>
                    <p className="text-sm text-powder-600">{userAnimal.longDescription}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-powder-200"
        >
          <ColorPicker 
            onColorChange={handleColorChange}
            className="w-full"
          />
        </motion.div>

        {/* Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-powder-200"
        >
          <h2 className="text-xl font-semibold text-powder-800 mb-4">
            {user.userType === 'instructor' ? 'Your Teaching Stats' : 'Your Forest Stats'}
          </h2>
          
          {user.userType === 'instructor' ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-sage-50 rounded-lg border border-sage-200">
                <div className="text-2xl font-bold text-sage-700">0</div>
                <div className="text-sm text-sage-600">Active Forests</div>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">0</div>
                <div className="text-sm text-blue-600">Total Students</div>
              </div>
              
              <div className="text-center p-4 bg-lavender-50 rounded-lg border border-lavender-200">
                <div className="text-2xl font-bold text-lavender-700">0%</div>
                <div className="text-sm text-lavender-600">Water Shared</div>
              </div>
              
              <div className="text-center p-4 bg-peach-50 rounded-lg border border-peach-200">
                <div className="text-2xl font-bold text-peach-700">0%</div>
                <div className="text-sm text-peach-600">Trees Mature</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-sage-50 rounded-lg border border-sage-200">
                <div className="text-2xl font-bold text-sage-700">{user.seeds}</div>
                <div className="text-sm text-sage-600">Seeds Available</div>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{user.water}</div>
                <div className="text-sm text-blue-600">Water Available</div>
              </div>
              
              <div className="text-center p-4 bg-lavender-50 rounded-lg border border-lavender-200">
                <div className="text-2xl font-bold text-lavender-700">{user.totalSeedsPlanted}</div>
                <div className="text-sm text-lavender-600">Trees Planted</div>
              </div>
              
              <div className="text-center p-4 bg-peach-50 rounded-lg border border-peach-200">
                <div className="text-2xl font-bold text-peach-700">{user.totalWaterUsedOnOthers}</div>
                <div className="text-sm text-peach-600">Water Shared</div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Action Buttons - Moved up and simplified */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-lg p-6 border border-powder-200"
        >
          <h2 className="text-xl font-semibold text-powder-800 mb-4">Ready to Explore?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user.userType === 'instructor' ? (
              <>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    window.location.hash = 'teacher';
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Instructor Dashboard</span>
                  </div>
                </Button>

                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    console.log('User clicked Open Chatbot');
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>Chat with Guide</span>
                  </div>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    window.location.hash = 'forest';
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    <span>Visit Forest</span>
                  </div>
                </Button>

                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    console.log('User clicked Open Chatbot');
                  }}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>Chat with Guide</span>
              </div>
            </Button>
          </>
        )}
      </div>

      <div className="mt-4 text-center">
        {user.userType === 'student' && (
          <p className="text-sm text-powder-600">
            Class Code: <span className="font-mono font-medium text-powder-800">{user.classCode}</span>
          </p>
        )}
        {user.userType === 'instructor' && user.isActive === false && (
          <p className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
            Your instructor account is pending approval. You'll receive an email when activated.
          </p>
        )}
      </div>
      </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalProfile;
